import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Clock,
  ArrowsClockwise,
  Warning,
  Package,
  ArrowSquareOut,
  MagnifyingGlass,
  ShieldCheck,
  Eye,
} from '@phosphor-icons/react';
import {
  apiClient,
  API_ENDPOINTS,
  Button,
  Spinner,
  Badge,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';

interface SyncedProductItem {
  productId: string;
  title: string;
  price: number;
  handle: string;
  syncStatus: 'synced' | 'pending' | 'failed' | 'excluded';
  lastError: string | null;
  lastSyncedAt: string | null;
}

interface GoogleSyncStatusSummary {
  totalItems: number;
  syncedItems: number;
  pendingItems: number;
  failedItems: number;
  lastSyncedAt: string | null;
  gmcAccountId: string | null;
  products: SyncedProductItem[];
}

export function GoogleSyncStatusPanel() {
  const queryClient = useQueryClient();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);
  const { addNotification } = useNotificationStore();

  const [activeTab, setActiveTab] = useState<'ALL' | 'SYNCED' | 'FAILED' | 'PENDING'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: summary, isLoading } = useQuery<GoogleSyncStatusSummary>({
    queryKey: ['google-sync-status', activeStoreId],
    queryFn: () => apiClient.get<GoogleSyncStatusSummary>(API_ENDPOINTS.GOOGLE.SYNC_STATUS),
    enabled: !!activeStoreId,
    refetchInterval: 15000,
  });

  const syncNowMutation = useMutation({
    mutationFn: () => apiClient.post(API_ENDPOINTS.GOOGLE.SYNC_NOW),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Google Feed Sync Queued',
        message: 'Your product catalog sync to Google Merchant Center has started.',
      });
      queryClient.invalidateQueries({ queryKey: ['google-sync-status', activeStoreId] });
    },
    onError: (error: any) => {
      addNotification({
        type: 'error',
        title: 'Sync Failed',
        message: error.message || 'Could not trigger catalog sync.',
      });
    },
  });

  const products = summary?.products || [];

  const filteredProducts = useMemo(() => {
    return products.filter((item) => {
      const matchesSearch =
        !searchQuery ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.handle.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      if (activeTab === 'SYNCED') return item.syncStatus === 'synced';
      if (activeTab === 'FAILED') return item.syncStatus === 'failed';
      if (activeTab === 'PENDING') return item.syncStatus === 'pending';
      return true;
    });
  }, [products, searchQuery, activeTab]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!summary) return null;

  const gmcDiagnosticsUrl = `https://merchants.google.com/mc/products/diagnostics${
    summary.gmcAccountId ? `?a=${summary.gmcAccountId}` : ''
  }`;
  const gmcProductsListUrl = `https://merchants.google.com/mc/products/list${
    summary.gmcAccountId ? `?a=${summary.gmcAccountId}` : ''
  }`;

  return (
    <div className="space-y-4">
      {/* Informational Polaris Banner */}
      <div className="p-4 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl space-y-3 shadow-2xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F6F6F7] dark:bg-slate-800 text-[#202223] dark:text-white rounded-lg border border-[#E1E3E5] dark:border-slate-700">
              <ShieldCheck weight="fill" className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-bold text-[#202223] dark:text-white text-xs">
                How Google Product Verification & Visibility Works
              </h4>
              <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
                When you click <span className="font-semibold text-[#202223] dark:text-white">Sync Now</span>, catalog items are submitted directly into your Google Merchant Center feed.
              </p>
            </div>
          </div>

          <a
            href={gmcDiagnosticsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-semibold text-[#202223] dark:text-white bg-[#F6F6F7] dark:bg-slate-800 hover:bg-[#E1E3E5] border border-[#D2D5D9] dark:border-slate-700 rounded-lg transition-colors whitespace-nowrap"
          >
            Open GMC Diagnostics
            <ArrowSquareOut weight="bold" className="w-3.5 h-3.5 ml-1.5" />
          </a>
        </div>

        {/* 3-Step Process Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 pt-3 border-t border-[#E1E3E5] dark:border-slate-800">
          <div className="bg-[#F6F6F7]/60 dark:bg-slate-800/40 p-3 rounded-lg border border-[#E1E3E5] dark:border-slate-800 flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300 font-bold text-[11px] flex items-center justify-center shrink-0">
              1
            </span>
            <div>
              <p className="text-xs font-bold text-[#202223] dark:text-white">API Feed Submission</p>
              <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Catalog data (prices, titles, images) is pushed directly to Google.
              </p>
            </div>
          </div>

          <div className="bg-[#F6F6F7]/60 dark:bg-slate-800/40 p-3 rounded-lg border border-[#E1E3E5] dark:border-slate-800 flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 font-bold text-[11px] flex items-center justify-center shrink-0">
              2
            </span>
            <div>
              <p className="text-xs font-bold text-[#202223] dark:text-white">Automated Google Review</p>
              <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Google verifies store policies and prices (usually 24–48 hours).
              </p>
            </div>
          </div>

          <div className="bg-[#F6F6F7]/60 dark:bg-slate-800/40 p-3 rounded-lg border border-[#E1E3E5] dark:border-slate-800 flex items-start gap-2.5">
            <span className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-950 text-purple-800 dark:text-purple-300 font-bold text-[11px] flex items-center justify-center shrink-0">
              3
            </span>
            <div>
              <p className="text-xs font-bold text-[#202223] dark:text-white">Live on Search & Ads</p>
              <p className="text-[11px] text-[#5C5F62] dark:text-slate-400 mt-0.5">
                Approved listings go live across Google Shopping and search.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Counts Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          icon={Package}
          label="Total Catalog Items"
          value={summary.totalItems}
          tone="neutral"
        />
        <StatTile
          icon={CheckCircle}
          label="Synced to Feed"
          value={summary.syncedItems}
          tone="success"
        />
        <StatTile
          icon={Clock}
          label="Pending Queue"
          value={summary.pendingItems}
          tone="warning"
        />
        <StatTile
          icon={Warning}
          label="Needs Attention"
          value={summary.failedItems}
          tone={summary.failedItems > 0 ? 'error' : 'neutral'}
        />
      </div>

      {/* Sync Action & Metadata Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-3.5 bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl shadow-2xs">
        <div className="text-xs text-[#5C5F62] dark:text-slate-400 flex items-center gap-2">
          <Clock weight="fill" className="w-3.5 h-3.5 text-[#5C5F62]" />
          {summary.lastSyncedAt ? (
            <span>
              Last sync completed <strong className="text-[#202223] dark:text-white">{new Date(summary.lastSyncedAt).toLocaleString()}</strong>
            </span>
          ) : (
            <span>Catalog ready for initial Google push</span>
          )}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <a
            href={gmcProductsListUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 sm:flex-initial inline-flex items-center justify-center h-8 px-3 text-xs font-semibold text-[#202223] dark:text-white bg-[#F6F6F7] dark:bg-slate-800 hover:bg-[#E1E3E5] border border-[#D2D5D9] dark:border-slate-700 rounded-lg transition-colors whitespace-nowrap"
          >
            View Listings in GMC
            <ArrowSquareOut weight="bold" className="w-3.5 h-3.5 ml-1.5 text-[#5C5F62]" />
          </a>

          <Button
            onClick={() => syncNowMutation.mutate()}
            disabled={syncNowMutation.isPending}
            className="flex-1 sm:flex-initial h-8 px-4 bg-[#202223] dark:bg-white hover:opacity-90 text-white dark:text-slate-900 font-bold rounded-lg text-xs"
          >
            {syncNowMutation.isPending ? (
              <>
                <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Syncing Feed…
              </>
            ) : (
              <>
                <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1.5" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Inspector Table */}
      <div className="bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-800 rounded-xl overflow-hidden shadow-2xs">
        <div className="p-3.5 border-b border-[#E1E3E5] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#F6F6F7]/60 dark:bg-slate-800/50">
          <div className="flex items-center bg-[#E1E3E5]/60 dark:bg-slate-800 p-0.5 rounded-lg">
            {(
              [
                { id: 'ALL', label: `All (${summary.totalItems})` },
                { id: 'SYNCED', label: `Synced (${summary.syncedItems})` },
                { id: 'FAILED', label: `Issues (${summary.failedItems})` },
                { id: 'PENDING', label: `Pending (${summary.pendingItems})` },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-900 text-[#202223] dark:text-white shadow-2xs'
                    : 'text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="relative w-full sm:w-64">
            <MagnifyingGlass className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C5F62]" />
            <input
              type="text"
              placeholder="Search synced products…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-8 pl-8 pr-3 text-xs bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-700 rounded-lg focus:outline-none text-[#202223] dark:text-white"
            />
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="py-12 text-center space-y-1.5">
            <Package weight="fill" className="w-8 h-8 text-[#9EA3A7] mx-auto" />
            <p className="text-xs font-semibold text-[#202223] dark:text-white">No matching products found</p>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400">
              {searchQuery ? 'Try clearing your search query' : 'Your store products will appear here after sync.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E3E5] dark:border-slate-800 bg-[#F6F6F7] dark:bg-slate-800/40 text-[11px] font-bold uppercase tracking-wider text-[#5C5F62] dark:text-slate-400">
                  <th className="py-2.5 px-4">Product Name</th>
                  <th className="py-2.5 px-4">Feed Status</th>
                  <th className="py-2.5 px-4">Verification & Visibility State</th>
                  <th className="py-2.5 px-4 text-right">Price (KES)</th>
                  <th className="py-2.5 px-4 text-right">Google Link</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E3E5] dark:divide-slate-800 text-xs">
                {filteredProducts.map((item) => {
                  const isSynced = item.syncStatus === 'synced';
                  const isFailed = item.syncStatus === 'failed';

                  return (
                    <tr key={item.productId} className="hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="py-3 px-4 font-medium text-[#202223] dark:text-white">
                        <div>
                          <p className="font-semibold text-[#202223] dark:text-white">{item.title}</p>
                          <p className="text-[11px] text-[#5C5F62] mt-0.5">/{item.handle}</p>
                        </div>
                      </td>

                      <td className="py-3 px-4">
                        {isSynced && (
                          <Badge variant="success" className="text-[11px] font-bold px-2 py-0.5">
                            <CheckCircle weight="fill" className="w-3 h-3 mr-1" />
                            Submitted to GMC
                          </Badge>
                        )}
                        {isFailed && (
                          <Badge variant="destructive" className="text-[11px] font-bold px-2 py-0.5">
                            <XCircle weight="fill" className="w-3 h-3 mr-1" />
                            Feed Rejection
                          </Badge>
                        )}
                        {item.syncStatus === 'pending' && (
                          <Badge variant="warning" className="text-[11px] font-bold px-2 py-0.5">
                            <Clock weight="fill" className="w-3 h-3 mr-1" />
                            Queued
                          </Badge>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        {isSynced ? (
                          <div className="space-y-0.5">
                            <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                              <Eye weight="fill" className="w-3.5 h-3.5 text-emerald-600" />
                              Active in GMC Feed
                            </p>
                            <p className="text-[11px] text-[#5C5F62] dark:text-slate-400">
                              Subject to Google's 24–48h policy review
                            </p>
                          </div>
                        ) : isFailed ? (
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-red-600 dark:text-red-400">
                              {item.lastError || 'Disapproved by Google'}
                            </p>
                            <p className="text-[11px] text-[#5C5F62] dark:text-slate-400">
                              Check Google Diagnostics for policy details
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-[#5C5F62] italic">Waiting for next sync cycle</span>
                        )}
                      </td>

                      <td className="py-3 px-4 text-right font-semibold text-[#202223] dark:text-white">
                        KES {item.price.toLocaleString()}
                      </td>

                      <td className="py-3 px-4 text-right">
                        <a
                          href={gmcProductsListUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs font-semibold text-[#202223] dark:text-white hover:underline transition-colors"
                        >
                          Inspect in Google
                          <ArrowSquareOut weight="bold" className="w-3 h-3 ml-1" />
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const TONE_STYLES = {
  neutral: { bg: 'bg-white dark:bg-[#1A1D21] border-[#D2D5D9] dark:border-slate-800', text: 'text-[#202223] dark:text-white', iconText: 'text-[#5C5F62]' },
  success: { bg: 'bg-white dark:bg-[#1A1D21] border-[#D2D5D9] dark:border-slate-800', text: 'text-emerald-800 dark:text-emerald-300', iconText: 'text-emerald-600' },
  warning: { bg: 'bg-white dark:bg-[#1A1D21] border-[#D2D5D9] dark:border-slate-800', text: 'text-amber-800 dark:text-amber-300', iconText: 'text-amber-600' },
  error: { bg: 'bg-white dark:bg-[#1A1D21] border-[#D2D5D9] dark:border-slate-800', text: 'text-red-800 dark:text-red-300', iconText: 'text-red-600' },
} as const;

function StatTile({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Package;
  label: string;
  value: number;
  tone: keyof typeof TONE_STYLES;
}) {
  const styles = TONE_STYLES[tone];
  return (
    <div className={`${styles.bg} border rounded-xl p-3.5 space-y-1 shadow-2xs`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-[#5C5F62] font-semibold">{label}</span>
        <Icon weight="fill" className={`w-3.5 h-3.5 ${styles.iconText}`} />
      </div>
      <p className={`text-xl font-bold tracking-tight ${styles.text}`}>{value}</p>
    </div>
  );
}
